import { getFirestore, collection, doc, addDoc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { secureGetItem } from "../../../data/utils";

export class LogVariantService {
    static async createLogVariant(variantId) {
        if (!variantId) {
            console.error("ID variante non valido:", variantId);
            throw new Error("ID variante non valido");
        }

        try {
            const db = getFirestore();
            const userEmail = secureGetItem("email");
            const userRole = secureGetItem("role");

            if (!userEmail) {
                console.error("Email utente non trovata");
                throw new Error("Email utente non trovata");
            }

            // Recupera i dati della variante per estrarre le informazioni necessarie
            const variantRef = doc(db, "Variants", variantId);
            const variantDoc = await getDoc(variantRef);

            if (!variantDoc.exists()) {
                console.error(`La variante ${variantId} non esiste`);
                throw new Error("Variante non trovata");
            }

            const variantData = variantDoc.data();

            // Recupero del nome del prodotto
            let productName = "";
            if (variantData.productRef) {
                const productRef = doc(db, "Products", variantData.productRef);
                const productDoc = await getDoc(productRef);
                if (productDoc.exists()) {
                    productName = productDoc.data().modelName || "";
                }
            }

            // Struttura base del log con le nuove proprietà
            const logData = {
                list_decline_variant: [],     // Rifiuti: account, date, role
                list_init_variant: [],        // Inizializzazioni: account, date
                list_validate_variant: [],    // Validazioni: account, date, role
                list_variant_assigned: [],    // Assegnazioni: account, assignedAccount, date
                logStatus: "Pending",         // Diventa "Complete" quando va in "In Pubblicazione"
                variantId: variantId,         // ID della variante per tracciabilità
                productName: productName,     // Nome del prodotto associato
                sku: variantData.skuModel || "" // SKU della variante
            };

            // console.log(`Creazione documento LogVariant per variante ${variantId}`);

            // Crea documento e aggiorna riferimenti
            const logRef = await addDoc(collection(db, "LogVariant"), logData);
            // console.log(`Documento creato con ID: ${logRef.id}`);

            await updateDoc(variantRef, {
                refLogVariant: logRef.id
            });
            // console.log(`Variante ${variantId} aggiornata con reference al log ${logRef.id}`);

            await updateDoc(doc(db, "LogVariant", logRef.id), {
                logCreationDate: Timestamp.now()
            });
            // console.log(`Data di creazione aggiornata per log ${logRef.id}`);

            return logRef.id;
        } catch (error) {
            console.error(`Errore nella creazione del log per la variante ${variantId}:`, error);
            throw error;
        }
    }

    static async getOrCreateLogVariantRef(variantId) {
        if (!variantId) {
            console.error("ID variante non valido:", variantId);
            throw new Error("ID variante non valido");
        }

        try {
            // console.log(`Recupero/creazione log per variante ${variantId}`);
            const db = getFirestore();
            const variantRef = doc(db, "Variants", variantId);
            const variantDoc = await getDoc(variantRef);

            if (!variantDoc.exists()) {
                console.error(`La variante ${variantId} non esiste`);
                throw new Error("Variante non trovata");
            }

            const variantData = variantDoc.data();

            // Verifica se esiste già un log
            if (variantData.refLogVariant) {
                // console.log(`Log esistente trovato: ${variantData.refLogVariant}`);
                const logDoc = await getDoc(doc(db, "LogVariant", variantData.refLogVariant));
                if (logDoc.exists()) {
                    return variantData.refLogVariant;
                } else {
                    // console.log(`Il log ${variantData.refLogVariant} non esiste più, ne creo uno nuovo`);
                }
            }

            // Crea nuovo log se necessario
            // console.log(`Nessun log esistente, creo un nuovo log per ${variantId}`);
            return await this.createLogVariant(variantId);
        } catch (error) {
            console.error(`Errore nel recupero/creazione del log per la variante ${variantId}:`, error);
            throw error;
        }
    }

    static async logAssignVariant(variantId, assignedAccount) {
        if (!variantId || !assignedAccount) {
            console.error("Parametri non validi:", { variantId, assignedAccount });
            throw new Error("Parametri non validi per l'assegnazione");
        }

        try {
            // console.log(`Assegnazione variante ${variantId} a ${assignedAccount}`);
            const db = getFirestore();
            const userEmail = secureGetItem("email");

            if (!userEmail) {
                console.error("Email utente non trovata");
                throw new Error("Email utente non trovata");
            }

            // Ottiene o crea log
            const logId = await this.getOrCreateLogVariantRef(variantId);
            // console.log(`Documento log per variante ${variantId}: ${logId}`);

            const logRef = doc(db, "LogVariant", logId);
            const logDoc = await getDoc(logRef);

            if (!logDoc.exists()) {
                console.error(`Log ${logId} non trovato`);
                throw new Error("Log non trovato");
            }

            const logData = logDoc.data();
            const variantAssigned = logData.list_variant_assigned || [];
            const currentTime = Timestamp.now();

            // Aggiunge record di assegnazione
            variantAssigned.push({
                account: userEmail,                   // Chi assegna
                assignedAccount: assignedAccount,     // A chi viene assegnato
                time: currentTime                      // Quando
            });

            // Recupera i dati della variante per aggiornare eventuali informazioni mancanti
            const variantRef = doc(db, "Variants", variantId);
            const variantDoc = await getDoc(variantRef);

            // Prepara l'aggiornamento
            const updateData = {
                list_variant_assigned: variantAssigned,
                logCreationDate: currentTime // Data di assegnazione
            };

            // Se la variante esiste, aggiungiamo le informazioni mancanti
            if (variantDoc.exists()) {
                const variantData = variantDoc.data();

                // Aggiorna productName se non è già presente nel log
                if (!logData.productName && variantData.glassesName) {
                    updateData.productName = variantData.glassesName;
                }

                // Aggiorna sku se non è già presente nel log
                if (!logData.sku && variantData.skuModel) {
                    updateData.sku = variantData.skuModel;
                }
            }

            // console.log(`Aggiornamento log ${logId} con nuova assegnazione`);

            // Aggiorna documento
            await updateDoc(logRef, updateData);

            // console.log(`Log aggiornato con successo per variante ${variantId}`);
        } catch (error) {
            console.error(`Errore nella registrazione dell'assegnazione della variante ${variantId}:`, error);
            throw error;
        }
    }

    static async logInitVariant(variantId) {
        if (!variantId) {
            console.error("ID variante non valido:", variantId);
            throw new Error("ID variante non valido");
        }

        try {
            // console.log(`Inizializzazione variante ${variantId}`);
            const db = getFirestore();
            const userEmail = secureGetItem("email");

            if (!userEmail) {
                console.error("Email utente non trovata");
                throw new Error("Email utente non trovata");
            }

            const logId = await this.getOrCreateLogVariantRef(variantId);
            const logRef = doc(db, "LogVariant", logId);
            const logDoc = await getDoc(logRef);

            if (!logDoc.exists()) {
                console.error(`Log ${logId} non trovato`);
                throw new Error("Log non trovato");
            }

            const logData = logDoc.data();
            const initVariant = logData.list_init_variant || [];
            const currentTime = Timestamp.now();

            // Aggiunge record di inizializzazione
            initVariant.push({
                account: userEmail,                // Chi inizializza
                time: currentTime                  // Quando
            });

            // Recupera i dati della variante per aggiornare eventuali informazioni mancanti
            const variantRef = doc(db, "Variants", variantId);
            const variantDoc = await getDoc(variantRef);

            // Prepara l'aggiornamento
            const updateData = {
                list_init_variant: initVariant,
                logInitVariantDate: currentTime // Data di inizio
            };

            // Se la variante esiste, aggiungiamo le informazioni mancanti
            if (variantDoc.exists()) {
                const variantData = variantDoc.data();

                // Aggiorna productName se non è già presente nel log
                if (!logData.productName && variantData.glassesName) {
                    updateData.productName = variantData.glassesName;
                }

                // Aggiorna sku se non è già presente nel log
                if (!logData.sku && variantData.skuModel) {
                    updateData.sku = variantData.skuModel;
                }
            }

            // console.log(`Aggiornamento log ${logId} con nuova inizializzazione`);

            // Aggiorna documento
            await updateDoc(logRef, updateData);

            // console.log(`Log aggiornato con successo per variante ${variantId}`);
        } catch (error) {
            console.error(`Errore nella registrazione dell'inizializzazione della variante ${variantId}:`, error);
            throw error;
        }
    }

    static async logValidateVariant(variantId, newStatus, modellingData = null) {
        if (!variantId) {
            console.error("ID variante non valido:", variantId);
            throw new Error("ID variante non valido");
        }

        try {
            // console.log(`Validazione variante ${variantId} con stato ${newStatus} e dati di modellazione:`, modellingData);
            const db = getFirestore();
            const userEmail = secureGetItem("email");
            const userRole = secureGetItem("role");

            if (!userEmail) {
                console.error("Email utente non trovata");
                throw new Error("Email utente non trovata");
            }

            const logId = await this.getOrCreateLogVariantRef(variantId);
            const logRef = doc(db, "LogVariant", logId);
            const logDoc = await getDoc(logRef);

            if (!logDoc.exists()) {
                console.error(`Log ${logId} non trovato`);
                throw new Error("Log non trovato");
            }

            const logData = logDoc.data();
            const validateVariant = logData.list_validate_variant || [];
            const currentTime = Timestamp.now();

            // Crea l'oggetto di validazione con i campi obbligatori
            const validationEntry = {
                account: userEmail,                // Chi valida
                time: currentTime,                 // Quando
                role: userRole,                    // Con quale ruolo
                modeling_note: "Empty"             // Default a "Empty" nel caso non venga specificato
            };

            // Aggiungi i dati di modellazione se forniti
            if (modellingData) {
                if (modellingData.type_of_modeling) {
                    validationEntry.type_of_modeling = modellingData.type_of_modeling;
                }

                // Sovrascrivi la nota di modellazione se fornita
                if (modellingData.modeling_note !== undefined) {
                    validationEntry.modeling_note = modellingData.modeling_note;
                }

                // Se è una pubblicazione, usa i dati forniti dal modellingData
                if (modellingData.modeling_note === "Published in ARS Library") {
                    validationEntry.account = modellingData.account;
                    validationEntry.role = modellingData.role;
                    validationEntry.time = modellingData.time;
                    validationEntry.modeling_note = "Published in ARS Library";
                }
            }

            // Debug per verificare che il campo sia effettivamente incluso
            validateVariant.push(validationEntry);

            // Recupera i dati della variante per aggiornare eventuali informazioni mancanti
            const variantRef = doc(db, "Variants", variantId);
            const variantDoc = await getDoc(variantRef);

            // Prepara aggiornamento
            const updateData = {
                list_validate_variant: validateVariant
            };

            // Se la variante esiste, aggiungiamo le informazioni mancanti
            if (variantDoc.exists()) {
                const variantData = variantDoc.data();

                // Aggiorna productName se non è già presente nel log
                if (!logData.productName && variantData.glassesName) {
                    updateData.productName = variantData.glassesName;
                }

                // Aggiorna sku se non è già presente nel log
                if (!logData.sku && variantData.skuModel) {
                    updateData.sku = variantData.skuModel;
                }
            }

            // Gestisce stato "In Pubblicazione"
            if (newStatus === "In Pubblicazione") {
                updateData.logStatus = "Complete";
                updateData.logCompleteVariantDate = currentTime;
            }

            // console.log(`Aggiornamento log ${logId} con dati:`, JSON.stringify(updateData, null, 2));

            // Aggiorna documento
            await updateDoc(logRef, updateData);

            // console.log(`Log aggiornato con successo per variante ${variantId}. ID Log: ${logId}`);
            return logId;
        } catch (error) {
            console.error(`Errore nella registrazione della validazione della variante ${variantId}:`, error);
            throw error;
        }
    }

    static async logDeclineVariant(variantId, declineInfo = null) {
        if (!variantId) {
            console.error("ID variante non valido:", variantId);
            throw new Error("ID variante non valido");
        }

        try {
            // console.log(`Rifiuto variante ${variantId}`);
            const db = getFirestore();
            const userEmail = secureGetItem("email");
            const userRole = secureGetItem("role");

            if (!userEmail) {
                console.error("Email utente non trovata");
                throw new Error("Email utente non trovata");
            }

            const logId = await this.getOrCreateLogVariantRef(variantId);
            const logRef = doc(db, "LogVariant", logId);
            const logDoc = await getDoc(logRef);

            if (!logDoc.exists()) {
                console.error(`Log ${logId} non trovato`);
                throw new Error("Log non trovato");
            }

            const logData = logDoc.data();
            const declineVariant = logData.list_decline_variant || [];
            const currentTime = Timestamp.now();

            // Aggiunge record di rifiuto, solo con account, role e time
            const declineEntry = {
                account: userEmail,                // Chi rifiuta
                time: currentTime,                 // Quando
                role: userRole                     // Con quale ruolo
            };

            // Aggiunge solo la nota se fornita
            if (declineInfo && declineInfo.note) {
                declineEntry.note = declineInfo.note;
            }

            declineVariant.push(declineEntry);

            // console.log(`Aggiornamento log ${logId} con nuovo rifiuto`);

            // Prepara l'aggiornamento
            const updateData = {
                list_decline_variant: declineVariant
            };

            // Aggiorna anche le informazioni aggiuntive se non sono già presenti
            if (declineInfo) {
                // Aggiorna productName se fornito e se non è già presente nel log
                if (declineInfo.nameProduct && !logData.productName) {
                    updateData.productName = declineInfo.nameProduct;
                }

                // Aggiorna sku se fornito e se non è già presente nel log
                if (declineInfo.skuModel && !logData.sku) {
                    updateData.sku = declineInfo.skuModel;
                }
            }

            // Aggiorna documento
            await updateDoc(logRef, updateData);

            // console.log(`Log aggiornato con successo per variante ${variantId}`);
        } catch (error) {
            console.error(`Errore nella registrazione del rifiuto della variante ${variantId}:`, error);
            throw error;
        }
    }
} 